import { JSONObject } from '../../types/types';
import { CustomJsonataFunctions } from './resolveStringExpression';
import { resolveStringPlaceholders } from './resolveStringPlaceholders';

/**
 * Recursively resolves JSONata placeholders in a JSON template object or array.
 *
 * - For each string in values, resolves any placeholders using the given jsonData.
 * - Arrays and objects are deeply traversed and resolved.
 * - Non-string primitives (numbers, booleans, null) are left unchanged.
 *
 * Optional `customFunctions` are forwarded down to the JSONata evaluator so
 * callers can expose extra `$name(...)` bindings (e.g. `$apiLookup`).
 *
 * Returns a new object/array with all string placeholders evaluated according to the provided data.
 */
export const resolveTemplate = async <T = unknown>(
  jsonTemplate: T,
  jsonData: JSONObject,
  customFunctions?: CustomJsonataFunctions
): Promise<T> => {
  const resolveNode = async (node: unknown): Promise<unknown> => {
    // If the value is a string, resolve its placeholders asynchronously
    if (typeof node === 'string') {
      return await resolveStringPlaceholders(node, jsonData, customFunctions);
    }
    // If the value is an array, recursively resolve placeholders for each element
    if (Array.isArray(node)) {
      return await Promise.all(node.map(resolveNode));
    }
    // If the value is an object, recursively resolve placeholders for each property
    if (node && typeof node === 'object') {
      const result: Record<string, unknown> = {};
      const promiseArray: Promise<unknown>[] = [];

      Object.entries(node as Record<string, unknown>).forEach(
        ([key, value]) => {
          const promise = resolveNode(value).then((resolvedNode) => {
            result[key] = resolvedNode;
          });
          promiseArray.push(promise);
        }
      );

      await Promise.all(promiseArray);
      return result;
    }
    // Numbers, booleans, null, undefined remain unchanged
    return node;
  };
  return (await resolveNode(jsonTemplate)) as T;
};
